import classnames from "classnames";
import { useEffect, useState } from "react";
import styles from "./HeroGalleryBadges.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";
import { getFeature } from "../../utils/features";
import { getShortValue, getShortScale } from "../../utils/readableNumbers";
import { setSaveCount } from "../../analytics/pageLoad";

const DEFAULT_SAVE_THRESHOLD = 50;

export default function HeroGalleryBadges({
  className,
}: {
  className?: string;
}) {
  const { product, getTranslation } = useRuntimeConstants() as {
    product: { sellingFast?: boolean };
    getTranslation: (key: string) => string;
  };
  const { isInStock } = useIsInStockContext() as { isInStock: boolean | null };

  const showSellingFast = isInStock === true && Boolean(product.sellingFast);

  return showSellingFast ? (
    <HeroGalleryBadge className={className}>
      {getTranslation("pdp_misc_selling_fast")}
    </HeroGalleryBadge>
  ) : (
    <HeroGallerySaveCount className={className} />
  );
}

function HeroGallerySaveCount({ className }: { className?: string }) {
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };
  const { isInStock } = useIsInStockContext() as { isInStock: boolean | null };
  const {
    webContext: { storeId },
    product: { saveCount },
    getTranslation,
  } = useRuntimeConstants() as {
    webContext: { storeId: string };
    product: {
      saveCount?: { canShowSaveCount?: boolean; value?: number };
    };
    getTranslation: (
      key: string,
      variables?: Record<string, unknown>,
    ) => string;
  };

  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [saveThreshold, setSaveThreshold] = useState(DEFAULT_SAVE_THRESHOLD);

  useEffect(() => {
    if (areFeaturesReady && isInStock === true) {
      const feature = getFeature("pdp-save-count");
      setIsFeatureEnabled(feature.isEnabled());
      setSaveThreshold(
        Number(feature.getVariables()?.thresholdSavesToShow) ||
          DEFAULT_SAVE_THRESHOLD,
      );
    }
  }, [areFeaturesReady, isInStock]);

  const saveCountValue = saveCount?.value || 0;

  const showSaveCount =
    isInStock === true &&
    isFeatureEnabled &&
    Boolean(saveCount?.canShowSaveCount) &&
    saveCountValue >= saveThreshold;

  useEffect(() => {
    if (showSaveCount) {
      setSaveCount(saveCountValue);
    }
  }, [showSaveCount, saveCountValue]);

  if (!showSaveCount) {
    return null;
  }

  const getSaveCountString = (value: number) => {
    switch (getShortScale(value, storeId)) {
      case "K":
        return getTranslation("pdp_saved_count_social_proofing_thousands", {
          saveCount: getShortValue(value, storeId),
        });
      case "M":
        return getTranslation("pdp_saved_count_social_proofing_millions", {
          saveCount: getShortValue(value, storeId),
        });
      default:
        return getShortValue(value, storeId).toString();
    }
  };

  const accessibilityText = getTranslation("pdp_saved_count_accessibility", {
    saveCount: saveCountValue,
  });

  return (
    <HeroGalleryBadge className={className}>
      <span aria-hidden="true">{getSaveCountString(saveCountValue)}</span>
      <span className={styles.visuallyHidden}>{accessibilityText}</span>
      <span className="product-saved-for-later-count" />
    </HeroGalleryBadge>
  );
}

function HeroGalleryBadge({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={classnames(styles.badge, className)} {...props}>
      {children}
    </div>
  );
}
